import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

const CATEGORY_OPTIONS = [
  { value: '', label: 'Tất cả phân loại' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'social', label: 'Social Media' },
  { value: 'content', label: 'Content' },
  { value: 'production', label: 'Sản xuất' },
  { value: 'report', label: 'Báo cáo' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'Tất cả độ ưu tiên' },
  { value: 'low', label: 'Thấp' },
  { value: 'medium', label: 'Trung bình' },
  { value: 'high', label: 'Cao' },
  { value: 'urgent', label: 'Khẩn cấp' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  { value: 'todo', label: 'Chờ xử lý' },
  { value: 'in_progress', label: 'Đang làm' },
  { value: 'review', label: 'Chờ review' },
  { value: 'done', label: 'Hoàn thành' },
];

const TaskFilters = ({ filters, onChange, users }) => {
  const handleChange = (field, value) => {
    onChange({ ...filters, [field]: value });
  };

  const handleClearFilters = () => {
    onChange({
      search: '',
      category: '',
      priority: '',
      status: '',
      assignee: '',
    });
  };

  const hasActiveFilters = 
    filters.search || 
    filters.category || 
    filters.priority || 
    filters.status || 
    filters.assignee;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 1.5, md: 2 },
        flexWrap: 'wrap',
        alignItems: { xs: 'stretch', md: 'center' },
        p: { xs: 1.5, md: 2.5 },
        bgcolor: 'white',
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      {/* Search */}
      <TextField
        placeholder="Tìm kiếm task..."
        size="small"
        value={filters.search || ''}
        onChange={(e) => handleChange('search', e.target.value)}
        sx={{
          flex: { xs: 1, md: 'none' },
          minWidth: { xs: '100%', md: 220 },
          '& .MuiOutlinedInput-root': {
            borderRadius: 2.5,
            bgcolor: '#f8fafc',
            '&:hover': { bgcolor: '#f1f5f9' },
            '&.Mui-focused': { bgcolor: 'white' },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#64748b' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Filter Group */}
      <Box sx={{ 
        display: 'flex', 
        gap: 1.5, 
        flexWrap: 'wrap',
        flex: { xs: 1, md: 'none' },
      }}>
        {/* Category Filter */}
        <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 130 } }}>
          <InputLabel>Phân loại</InputLabel>
          <Select
            value={filters.category || ''}
            onChange={(e) => handleChange('category', e.target.value)}
            label="Phân loại"
            sx={{ 
              borderRadius: 2,
              bgcolor: '#f8fafc',
              '&:hover': { bgcolor: '#f1f5f9' },
            }}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Priority Filter */}
        <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 130 } }}>
          <InputLabel>Ưu tiên</InputLabel>
          <Select
            value={filters.priority || ''}
            onChange={(e) => handleChange('priority', e.target.value)}
            label="Ưu tiên"
            sx={{ 
              borderRadius: 2,
              bgcolor: '#f8fafc',
              '&:hover': { bgcolor: '#f1f5f9' },
            }}
          >
            {PRIORITY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Status Filter */}
        <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 130 } }}>
          <InputLabel>Trạng thái</InputLabel>
          <Select
            value={filters.status || ''}
            onChange={(e) => handleChange('status', e.target.value)}
            label="Trạng thái"
            sx={{ 
              borderRadius: 2,
              bgcolor: '#f8fafc',
              '&:hover': { bgcolor: '#f1f5f9' },
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Assignee Filter */}
        <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 150 } }}>
          <InputLabel>Phụ trách</InputLabel>
          <Select
            value={filters.assignee || ''}
            onChange={(e) => handleChange('assignee', e.target.value)}
            label="Phụ trách"
            sx={{ 
              borderRadius: 2,
              bgcolor: '#f8fafc',
              '&:hover': { bgcolor: '#f1f5f9' },
            }}
          >
            <MenuItem value="">Tất cả</MenuItem>
            {(users || []).map((user) => (
              <MenuItem key={user._id} value={user._id}>
                {user.fullName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Clear Filters & Active Chips */}
      {hasActiveFilters && (
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          alignItems: 'center', 
          flexWrap: 'wrap',
          ml: { xs: 0, md: 'auto' },
        }}>
          {filters.category && (
            <Chip
              label={CATEGORY_OPTIONS.find((o) => o.value === filters.category)?.label}
              size="small"
              onDelete={() => handleChange('category', '')}
              sx={{ 
                bgcolor: '#ede9fe', 
                color: '#7c3aed',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': { color: '#7c3aed' },
              }}
            />
          )}
          {filters.priority && (
            <Chip
              label={PRIORITY_OPTIONS.find((o) => o.value === filters.priority)?.label}
              size="small"
              onDelete={() => handleChange('priority', '')}
              sx={{ 
                bgcolor: '#fef3c7', 
                color: '#d97706',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': { color: '#d97706' },
              }}
            />
          )}
          {filters.status && (
            <Chip
              label={STATUS_OPTIONS.find((o) => o.value === filters.status)?.label}
              size="small"
              onDelete={() => handleChange('status', '')}
              sx={{ 
                bgcolor: '#d1fae5', 
                color: '#059669',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': { color: '#059669' },
              }}
            />
          )}
          <Tooltip title="Xóa tất cả bộ lọc">
            <IconButton
              onClick={handleClearFilters}
              size="small"
              sx={{
                bgcolor: '#fee2e2',
                color: '#dc2626',
                '&:hover': { bgcolor: '#fecaca' },
              }}
            >
              <ClearIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default TaskFilters;
